/**
 * Backend API client for Fantomize
 *
 * Automatically adds Firebase authentication tokens to requests.
 * Provides methods for all backend API endpoints.
 */
import axios from 'axios';
import { auth } from '../config/firebase';
import API_URL from '../config/api';

// Create axios instance with centralized API URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor - adds Firebase token to all requests
 */
api.interceptors.request.use(
  async (config) => {
    // Use Firebase authentication
    if (auth) {
      const user = auth.currentUser;
      if (user) {
        try {
          const token = await user.getIdToken();
          config.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
          console.error('Failed to get auth token:', error);
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - handles common errors
 */
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - token expired or invalid
      console.error('Authentication failed. Please log in again.');
    }
    return Promise.reject(error);
  }
);

/**
 * Backend API methods
 */
export const backendAPI = {
  /**
   * Get current user profile and preferences
   */
  async getUserProfile() {
    const response = await api.get('/api/user/profile');
    return response.data;
  },

  /**
   * Update user preferences
   * @param {Object} preferences - User preference object
   */
  async updatePreferences(preferences) {
    const response = await api.post('/api/user/preferences', preferences);
    return response.data;
  },

  /**
   * Search for clothing with image (personalized if authenticated)
   * @param {File} imageFile - Image file to search with
   */
  async searchClothing(imageFile) {
    const formData = new FormData();
    formData.append('file', imageFile);

    const response = await api.post('/search', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Search products by keyword (trending styles, etc.)
   * @param {string} keyword - Search keyword
   * @param {number} limit - Number of results
   */
  async searchByKeyword(keyword, limit = 10) {
    const response = await api.get('/api/search_by_keyword', {
      params: { keyword, limit },
    });
    return response.data;
  },

  /**
   * Free text search for affiliate products with pagination
   * @param {string} query - Free text search query
   * @param {number} page - Page number (1-indexed)
   * @param {number} limit - Results per page (default: 10)
   * @returns {Object} - { success, results, count, page, has_more, ... }
   */
  async textSearch(query, page = 1, limit = 10) {
    const response = await api.get('/api/text-search', {
      params: { query, page, limit },
    });
    return response.data;
  },

  /**
   * Get all products (paginated)
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   */
  async getProducts(page = 1, limit = 20) {
    const response = await api.get('/api/products', {
      params: { page, limit },
    });
    return response.data;
  },

  /**
   * Get personalized "For You" recommendations based on user preferences
   * @param {number} limit - Number of recommendations (default: 3)
   */
  async getForYouRecommendations(limit = 3) {
    const response = await api.get('/api/for-you', {
      params: { limit },
    });
    return response.data;
  },

  /**
   * Track a search action
   * @param {Object} searchData - Search details {query, results_count}
   */
  async trackSearch(searchData) {
    const response = await api.post('/api/user/track-search', searchData);
    return response.data;
  },

  /**
   * Add item to favorites
   * @param {Object} item - Item details {url, name, image, price}
   */
  async addFavorite(item) {
    const response = await api.post('/api/user/favorites', item);
    return response.data;
  },

  /**
   * Remove item from favorites
   * @param {string} itemUrl - URL of the item to remove
   */
  async removeFavorite(itemUrl) {
    const response = await api.delete('/api/user/favorites', {
      params: { item_url: itemUrl },
    });
    return response.data;
  },

  /**
   * Track a match confirmation
   */
  async trackMatch() {
    const response = await api.post('/api/user/track-match');
    return response.data;
  },

  /**
   * Log user activity (fire-and-forget with silent error handling)
   * @param {string} type - Activity type: 'search', 'match', 'favorite', 'preference_update'
   * @param {string} title - Human-readable activity title
   * @param {Object} details - Optional additional details
   */
  logActivity(type, title, details = {}) {
    // Fire-and-forget: don't await, don't block UI
    api.post('/api/user/activity', { type, title, details })
      .catch(error => {
        // Silent fail - activity tracking should never disrupt UX
        console.debug('Activity tracking failed (non-critical):', error.message);
      });
  },

  /**
   * Get user's recent activities
   * @param {number} limit - Maximum number of activities (default: 15)
   */
  async getActivities(limit = 15) {
    const response = await api.get('/api/user/activities', {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Clear all user activities
   */
  async clearActivities() {
    const response = await api.delete('/api/user/activities');
    return response.data;
  },

  /**
   * Delete user account permanently
   * This action is irreversible and removes all user data
   */
  async deleteAccount() {
    const response = await api.delete('/api/auth/account');
    return response.data;
  },

  // ========== AI Stylist (Leo) ==========

  /**
   * Chat with AI stylist Leo
   * @param {string} message - User's message
   * @param {string|null} conversationId - Existing conversation ID or null for new
   * @param {string|null} imageBase64 - Optional base64 image data
   * @returns {Object} - { response, conversation_id, token_usage }
   */
  async chatWithStylist(message, conversationId = null, imageBase64 = null) {
    const payload = {
      message,
      conversation_id: conversationId,
    };

    if (imageBase64) {
      payload.image_base64 = imageBase64;
    }

    const response = await api.post('/chat/leo', payload);
    return response.data;
  },

  /**
   * Analyze an image with AI stylist
   * @param {string} imageBase64 - Base64 encoded image data
   * @returns {Object} - { success, analysis, description, style_suggestions, tokens_used }
   */
  async analyzeImage(imageBase64) {
    const response = await api.post('/api/stylist/analyze-image', {
      image_base64: imageBase64
    });
    return response.data;
  },

  // ========== Credits Management ==========

  /**
   * Get current token credit balance
   * @returns {Object} - { tokens_used_session, tokens_used_daily, session_remaining, daily_remaining, ... }
   */
  async getCreditsBalance() {
    const response = await api.get('/api/credits/balance');
    return response.data;
  },

  /**
   * Claim one-time free credits bonus (3000 credits)
   * @returns {Object} - { success, credits_added, message }
   */
  async claimFreeCredits() {
    const response = await api.post('/api/credits/claim-free');
    return response.data;
  },

  /**
   * Purchase credits via Stripe
   * @param {string} packageType - 'small' (10K), 'medium' (50K), or 'large' (100K)
   * @returns {Object} - { checkout_url, session_id, package, tokens, price_usd }
   */
  async purchaseCredits(packageType) {
    const response = await api.post('/api/credits/purchase', {
      package: packageType
    });
    return response.data;
  },
};

export default api;


